import React, { useEffect, useState } from 'react';
import ToolIcon from '../common/ToolIcon';
import { Droplets, ArrowLeft, ArrowRight, Plus, X, Trash2 } from 'lucide-react';
import ServiceCard from '../common/ServiceCard';
import { ScheduledServices } from '../../dados/services';

const dataScheduledServices = ScheduledServices

const AgendaScreen = ({ setActiveScreen }) => {
    const [selectedMonth, setSelectedMonth] = useState('Abril');
    const [selectedDate, setSelectedDate] = useState(6);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentService, setCurrentService] = useState(null);
    const [newService, setNewService] = useState({
        service: '',
        date: '',
        day: '',
        time: '',
        icon: 'garden'
    });
    // Lista de serviços agendados
    const [scheduledServices, setScheduledServices] = useState(dataScheduledServices.filter(service => service.CalendarDay === selectedDate));
    
    useEffect(() => {
      // Atualiza a lista de serviços agendados quando a data selecionada muda
      // E retorna a array em ordem cronologia com base no horario
      const filteredServices = ScheduledServices
        .filter(service => service.CalendarDay === selectedDate)
        .sort((a, b) => {
          const timeA = a.time.split(':')[0];
          const timeB = b.time.split(':')[0];
          return timeA - timeB;
        });

      if(filteredServices.length >= 1) {
        filteredServices.map(service => {
          service.status = 'scheduled';
        })
        filteredServices[0].status = 'upcoming';
      }
      setScheduledServices(filteredServices);
    }, [selectedDate, newService]);

    // Função para abrir o modal de edição
    const handleEditClick = (service) => {
      setCurrentService(service);
      setNewService({
        service: service.service,
        time: service.time,
        icon: service.color === 'blue' ? 'pool' : 'garden'
      });
      setShowEditModal(true);
    };
    
    // Função para atualizar um serviço existente
    const handleUpdateService = () => {
      if (!currentService) return;
      
      const updatedServices = scheduledServices.map(service => {
        if (service.id === currentService.id) {
          return {
            ...service,
            service: newService.service,
            time: newService.time,
            color: newService.icon === 'pool' ? 'blue' : 'green',
            icon: newService.icon === 'pool' 
              ? <Droplets className="text-blue-600" /> 
              : <ToolIcon className="text-green-600" />
          };
        }
        return service;
      });
      
      setScheduledServices(updatedServices);
      setShowEditModal(false);
      setCurrentService(null);
    };
    
    // Função para cancelar/remover um agendamento
    const handleCancelService = () => {
      if (!currentService) return;
      
      const filteredServices = scheduledServices.filter(
        service => service.id !== currentService.id
      );
      
      setScheduledServices(filteredServices);
      setShowEditModal(false);
      setCurrentService(null);
    };
    
    const checkHasService = (day) => {
      return dataScheduledServices.filter(service => service.CalendarDay === day).length > 0;
    }

    // Função para adicionar novo serviço
    const handleAddService = () => {
      // Formatar data selecionada
      const dayStr = selectedDate.toString().padStart(2, '0');
      const dateStr = `${dayStr} de ${selectedMonth}`;
      
      // Determinar o dia da semana (simplificado)
      // Em uma implementação real, usaríamos uma biblioteca como date-fns
      const weekdays = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
      const day = weekdays[new Date(2025, 3, selectedDate).getDay()];
      
      // Criar o novo serviço
      const newServiceObj = {
        id: scheduledServices.length + 1,
        CalendarDay: selectedDate,
        date: dateStr,
        day: day,
        service: newService.service,
        time: newService.time,
        status: 'scheduled',
        color: newService.icon === 'pool' ? 'blue' : 'green',
        icon: newService.icon === 'pool' 
          ? <Droplets className="text-blue-600" /> 
          : <ToolIcon className="text-green-600" />
      };
      
      // Adicionar ao array de serviços
      ScheduledServices.push(newServiceObj)
      
      // Fechar o modal e resetar o formulário
      setShowAddModal(false);
      setNewService({
        service: '',
        date: '',
        day: '',
        time: '',
        icon: 'garden'
      });
    };
    
    // Componente do dia do calendário
    const CalendarDay = ({ day, isToday, isSelected, isCurrentMonth, hasService, onClick }) => (
      <div 
        className={`
          relative w-8 h-8 flex items-center justify-center mx-auto
          ${isSelected ? 'bg-green-600 text-white' : isToday ? 'bg-gray-200' : ''}
          ${!isCurrentMonth ? 'text-gray-300' : ''}
          ${isCurrentMonth && !isSelected && !isToday ? 'hover:bg-gray-100' : ''}
          rounded-full cursor-pointer
        `}
        onClick={onClick}
      >
        {day}
        {hasService && !isSelected && (
          <div className="absolute bottom-0 w-1 h-1 bg-green-600 rounded-full"></div>
        )}
      </div>
    );
    
    // Renderiza o mini-calendário
    const renderCalendar = () => {
      // Dias da semana abreviados
      const weekdays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
      
      // Dias para o mês de abril (exemplo)
      // Na primeira linha são os últimos dias de março e em abril
      const days = [
        { day: 26, currentMonth: false, hasService: false },
        { day: 27, currentMonth: false, hasService: false },
        { day: 28, currentMonth: false, hasService: false },
        { day: 29, currentMonth: false, hasService: false },
        { day: 30, currentMonth: false, hasService: false },
        { day: 31, currentMonth: false, hasService: false },
        { day: 1, currentMonth: true, hasService: checkHasService(1) },
        { day: 2, currentMonth: true, hasService: checkHasService(2) },
        { day: 3, currentMonth: true, hasService: checkHasService(3) },
        { day: 4, currentMonth: true, hasService: checkHasService(4), today: true },
        { day: 5, currentMonth: true, hasService: checkHasService(5) },
        { day: 6, currentMonth: true, hasService: checkHasService(6) },
        { day: 7, currentMonth: true, hasService: checkHasService(7) },
        { day: 8, currentMonth: true, hasService: checkHasService(8) },
        { day: 9, currentMonth: true, hasService: checkHasService(9) },
        { day: 10, currentMonth: true, hasService: checkHasService(10) },
        { day: 11, currentMonth: true, hasService: checkHasService(11) },
        { day: 12, currentMonth: true, hasService: checkHasService(12) },
        { day: 13, currentMonth: true, hasService: checkHasService(13) },
        { day: 14, currentMonth: true, hasService: checkHasService(14) },
        { day: 15, currentMonth: true, hasService: checkHasService(15) },
        { day: 16, currentMonth: true, hasService: checkHasService(16) },
        { day: 17, currentMonth: true, hasService: checkHasService(17) },
        { day: 18, currentMonth: true, hasService: checkHasService(18) },
        { day: 19, currentMonth: true, hasService: checkHasService(19) },
        { day: 20, currentMonth: true, hasService: checkHasService(20) },
        { day: 21, currentMonth: true, hasService: checkHasService(21) },
        { day: 22, currentMonth: true, hasService: checkHasService(22) },
        { day: 23, currentMonth: true, hasService: checkHasService(23) },
        { day: 24, currentMonth: true, hasService: checkHasService(24) },
        { day: 25, currentMonth: true, hasService: checkHasService(25) },
        { day: 26, currentMonth: true, hasService: checkHasService(26) },
        { day: 27, currentMonth: true, hasService: checkHasService(27) },
        { day: 28, currentMonth: true, hasService: checkHasService(28) },
        { day: 29, currentMonth: true, hasService: checkHasService(29) },
        { day: 30, currentMonth: true, hasService: checkHasService(30) },
        { day: 1, currentMonth: false, hasService: false },
        { day: 2, currentMonth: false, hasService: false },
        { day: 3, currentMonth: false, hasService: false },
        { day: 4, currentMonth: false, hasService: false },
        { day: 5, currentMonth: false, hasService: false },
        { day: 6, currentMonth: false, hasService: false },
      ];
      
      return (
        <div>
          {/* Dias da semana */}
          <div className="grid grid-cols-7 text-center mb-2">
            {weekdays.map((day, idx) => (
              <div key={idx} className="text-xs text-gray-500">{day}</div>
            ))}
          </div>
          
          {/* Dias do mês */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {days.map((day, idx) => (
              <CalendarDay 
                key={idx}
                day={day.day}
                isToday={day.today}
                isSelected={day.currentMonth && day.day === selectedDate}
                isCurrentMonth={day.currentMonth}
                hasService={day.hasService}
                onClick={() => day.currentMonth && setSelectedDate(day.day)}
              />
            ))}
          </div>
        </div>
      );
    };
    
    // Modal para adicionar novo serviço
    const renderAddServiceModal = () => {
      if (!showAddModal) return null;
      
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-lg text-gray-800">Agendar Novo Serviço</h3>
              <button 
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                onClick={() => setShowAddModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Selecionada</label>
                <div className="text-gray-600 bg-gray-50 p-2 rounded">
                  {selectedDate} de {selectedMonth} de 2025
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Serviço</label>
                <select 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  value={newService.service}
                  onChange={(e) => setNewService({...newService, service: e.target.value, icon: e.target.value.includes('Piscina') ? 'pool' : 'garden'})}
                >
                  <option value="">Selecione um serviço</option>
                  <option value="Manutenção do Jardim">Manutenção do Jardim</option>
                  <option value="Limpeza da Piscina">Limpeza da Piscina</option>
                  <option value="Poda de Árvores">Poda de Árvores</option>
                  <option value="Tratamento do Gramado">Tratamento do Gramado</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Horário</label>
                <select 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  value={newService.time}
                  onChange={(e) => setNewService({...newService, time: e.target.value})}
                >
                  <option value="">Selecione um horário</option>
                  <option value="09:00 - 10:30">09:00 - 10:30</option>
                  <option value="10:00 - 11:30">10:00 - 11:30</option>
                  <option value="13:00 - 14:30">13:00 - 14:30</option>
                  <option value="14:00 - 16:00">14:00 - 16:00</option>
                  <option value="16:00 - 17:30">16:00 - 17:30</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancelar
                </button>
                <button
                  className={`px-4 py-2 bg-green-600 rounded-lg text-white ${
                    !newService.service || !newService.time ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'
                  }`}
                  disabled={!newService.service || !newService.time}
                  onClick={handleAddService}
                >
                  Agendar Serviço
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    };
    
    // Modal para editar/cancelar serviço
    const renderEditServiceModal = () => {
      if (!showEditModal || !currentService) return null;
      
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-lg text-gray-800">Editar Agendamento</h3>
              <button 
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                onClick={() => setShowEditModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Agendada</label>
                <div className="text-gray-600 bg-gray-50 p-2 rounded">
                  {currentService.date} de 2025
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Serviço</label>
                <select 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  value={newService.service}
                  onChange={(e) => setNewService({...newService, service: e.target.value, icon: e.target.value.includes('Piscina') ? 'pool' : 'garden'})}
                >
                  <option value="Manutenção do Jardim">Manutenção do Jardim</option>
                  <option value="Limpeza da Piscina">Limpeza da Piscina</option>
                  <option value="Poda de Árvores">Poda de Árvores</option>
                  <option value="Tratamento do Gramado">Tratamento do Gramado</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Horário</label>
                <select 
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  value={newService.time}
                  onChange={(e) => setNewService({...newService, time: e.target.value})}
                >
                  <option value="09:00 - 10:30">09:00 - 10:30</option>
                  <option value="10:00 - 11:30">10:00 - 11:30</option>
                  <option value="13:00 - 14:30">13:00 - 14:30</option>
                  <option value="14:00 - 16:00">14:00 - 16:00</option>
                  <option value="16:00 - 17:30">16:00 - 17:30</option>
                </select>
              </div>
              
              <div className="flex items-center justify-between mt-6">
                <button
                  className="flex items-center px-4 py-2 border border-red-300 rounded-lg text-red-600 hover:bg-red-50"
                  onClick={handleCancelService}
                >
                  <Trash2 size={16} className="mr-1" />
                  Cancelar Agendamento
                </button>
                
                <button
                  className="px-4 py-2 bg-green-600 rounded-lg text-white hover:bg-green-700"
                  onClick={handleUpdateService}
                >
                  Salvar Alterações
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    };
    
    // Componente de Card modificado para incluir botão de edição
    const ServiceCardWithEdit = ({ service }) => {
      return (
        <div className="bg-white rounded-xl shadow-sm p-4 flex items-start justify-between">
          <div className="flex">
            <div className="mr-3">
              {service.icon}
            </div>
            <div>
              <div className="flex items-center mb-1">
                <span className="text-sm font-medium text-gray-800">{service.date}</span>
                <span className="ml-2 text-xs text-gray-500">{service.day}</span>
              </div>
              <p className="text-sm font-medium text-gray-700">{service.service}</p>
              <p className="text-xs text-gray-500 mt-1">{service.time}</p>
              {service.status === 'upcoming' && (
                <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-xs font-medium text-green-800 rounded">Próximo</span>
              )}
            </div>
          </div>
          
          <button 
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            onClick={() => handleEditClick(service)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </button>
        </div>
      );
    };
    
    return (
      <div className="p-4 flex flex-col items-center">
        <div className="flex items-center mb-6 w-full">
          <ArrowLeft size={20} className="mr-2 cursor-pointer" onClick={() => setActiveScreen('home')} />
          <h2 className="text-xl font-bold text-gray-800">Minha Agenda</h2>
        </div>
        
        {/* Calendário mensal */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 lg:w-2/4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <h3 className="font-semibold text-lg text-gray-800">{selectedMonth} 2025</h3>
              <div className="ml-2 px-2 py-1 bg-green-100 rounded-md text-xs font-medium text-green-800">
                {ScheduledServices.length} serviços
              </div>
            </div>
            <div className="flex space-x-1">
              <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                <ArrowLeft size={16} />
              </button>
              <button className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
          
          {renderCalendar()}
          
          <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center">
              <div className="flex items-center mr-3">
                <span className="text-xs text-gray-600">Legenda: </span>
              </div>
              <div className="flex items-center mr-3">
                <div className="w-2 h-2 bg-green-600 rounded-full mr-1"></div>
                <span className="text-xs text-gray-600">Serviço agendado</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-gray-300 rounded-full mr-1"></div>
                <span className="text-xs text-gray-600">Hoje</span>
              </div>
            </div>
            <button 
              className="text-green-600 text-sm font-medium"
              onClick={() => setShowAddModal(true)}
            >
              Agendar novo serviço
            </button>
          </div>
        </div>
        
        {/* Serviços agendados */}
        <h3 className="font-semibold text-lg text-gray-800 mb-3 w-full">Serviços Agendados</h3>
        
        <div className="space-y-3 mb-6 w-full">
          {
            scheduledServices.length === 0 && (
              <div className="bg-gray-100 text-gray-500 p-4 rounded-lg text-center">
                Nenhum serviço agendado para esta data.
              </div>
            )
          }
          {scheduledServices.map((service) => (
            <ServiceCardWithEdit 
              key={service.id}
              service={service}
            />
          ))}
        </div>
        
        {/* Botão de adicionar */}
        <button 
          className="fixed bottom-20 right-4 w-12 h-12 bg-green-600 rounded-full flex items-center justify-center shadow-lg"
          onClick={() => setShowAddModal(true)}
        >
          <Plus className="text-white" />
        </button>
        
        {/* Modal de adicionar serviço */}
        {renderAddServiceModal()}
        
        {/* Modal de editar serviço */}
        {renderEditServiceModal()}
      </div>
    );
  };

export default AgendaScreen;